import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { UiButtonFunctionComponent, UiButtonIconComponent } from '@cynteka/ui-kit';
import {
    animationFrameScheduler,
    distinctUntilChanged,
    map,
    Observable,
    observeOn,
    startWith,
    Subject,
    takeUntil,
} from 'rxjs';

import { LimitRecordException, MicrophoneProcessor } from '../../../core/microphone-processor';
import { ChatOutgoingMessage } from '../../../types/chat-session.types';
import { AiChatAttachmentsAcceptedTypes } from 'src/app/models/ai-chat-attachment.model';
import { AiChatSessionStateStore } from 'src/app/state/ai-chat-session-state.store';
import { AiChatAlertService } from 'src/app/cyn/fck-chat-module/services/ai-chat-alert.service';

const MIN_VOICE_DURATION_MS = 300;
const MAX_VOICE_DURATION_MS = 60_000;
const VOICE_LIMIT_WARNING_AT_MS = 55_000;
const MAX_ATTACHED_FILES = 5;
const HOLD_TOOLTIP_DURATION_MS = 4_000;
const LIMIT_TOOLTIP_DURATION_MS = 10_000;
const HOLD_TOOLTIP_TEXT = 'Удерживайте для записи';
const LIMIT_TOOLTIP_TEXT = 'Лимит записи: 1 минута';
const VOICE_BIT_RATE = 64;

interface AttachedFileView {
    file: File;
    id: string;
}

@Component({
    selector: 'chat-input',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ReactiveFormsModule, UiButtonFunctionComponent, UiButtonIconComponent],
    templateUrl: './chat-input.component.html',
    styleUrls: ['./chat-input.component.scss'],
})
export class ChatInputComponent implements OnInit, OnDestroy {
    /** Событие отправки сообщения. */
    @Output()
    public readonly messageSent = new EventEmitter<ChatOutgoingMessage>();

    /** Событие отмены текущей генерации ответа. */
    @Output()
    public readonly cancelRequested = new EventEmitter<void>();

    /** Событие изменения статуса набора текста. */
    @Output()
    public readonly typingChanged = new EventEmitter<boolean>();

    /** true, когда вместо отправки нужно показать кнопку отмены генерации. */
    @Input()
    public showCancelButton = false;

    /** Разрешено ли прикрепление файлов. */
    @Input()
    public allowFiles = false;

    /** Разрешена ли запись голосовых сообщений. */
    @Input()
    public allowVoice = false;

    /** Контрол поля ввода сообщения. */
    public readonly textControl = new FormControl('', { nonNullable: true });

    /** Поток текста для скрытого измерителя. */
    public textFakeChanges$!: Observable<string>;

    /** Список прикреплённых файлов для простого preview. */
    public attachedFiles: AttachedFileView[] = [];

    /** true пока идёт запись голосового сообщения. */
    public isRecording = false;

    /** Подпись секундомера во время записи. */
    public recordingElapsedLabel = '0.0 с';

    /** true, когда нужно показать подсказку у микрофона. */
    public showHoldTooltip = false;

    /** Текст подсказки у микрофона. */
    public holdTooltipText = HOLD_TOOLTIP_TEXT;

    /** true, когда поле ввода перешло в многострочный режим. */
    public isExpanded = false;

    @HostBinding('class.chat-input--recording')
    public get hostRecordingClass(): boolean {
        return this.isRecording;
    }

    @ViewChild('textareaEl')
    private textareaEl?: ElementRef<HTMLTextAreaElement>;

    @ViewChild('textareaExpandedEl')
    private textareaExpandedEl?: ElementRef<HTMLTextAreaElement>;

    @ViewChild('fakeTextEl')
    private fakeTextEl?: ElementRef<HTMLDivElement>;

    @ViewChild('fileInputEl')
    private fileInputEl?: ElementRef<HTMLInputElement>;

    private readonly destroy$ = new Subject<void>();
    private processor: MicrophoneProcessor | null = null;
    private recordingStartTime: number | null = null;
    private docUpHandler: (() => void) | null = null;
    private docCancelHandler: (() => void) | null = null;
    private recordingTimerId: ReturnType<typeof setInterval> | null = null;
    private holdTooltipTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private limitWarningShown = false;
    private nextAttachedFileId = 0;

    public constructor(
        private readonly cdr: ChangeDetectorRef,
        private readonly zone: NgZone,
        private readonly stateStore: AiChatSessionStateStore,
        private readonly alertService: AiChatAlertService,
    ) {}

    public ngOnInit(): void {
        this.textFakeChanges$ = this.textControl.valueChanges.pipe(
            startWith(this.textControl.value),
            map((value: string) => value.endsWith('\n') ? `${value}\u200B` : value),
        );

        this.textControl.valueChanges.pipe(
            startWith(this.textControl.value),
            map((value: string) => value.trim().length > 0),
            distinctUntilChanged(),
            takeUntil(this.destroy$),
        ).subscribe((isTyping: boolean) => {
            this.typingChanged.emit(isTyping);
        });

        this.textControl.valueChanges.pipe(
            startWith(this.textControl.value),
            observeOn(animationFrameScheduler),
            takeUntil(this.destroy$),
        ).subscribe(() => {
            this.syncExpandedState();
            this.syncTextareaHeight();
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.removeDocumentListeners();
        this.stopRecordingTimer();
        this.clearHoldTooltipTimer();
        this.processor?.destroy();
    }

    /** Enter отправляет сообщение; во время stop/cancel — игнорируется. */
    public onEnter(event: Event): void {
        event.preventDefault();

        if (this.showCancelButton) {
            return;
        }

        this.send();
    }

    /** Отправляет сообщение или отменяет генерацию, если агент занят. */
    public send(): void {
        if (this.isRecording) {
            return;
        }

        if (this.showCancelButton) {
            this.cancel();
            return;
        }

        const text = this.textControl.value.trim();
        const files = this.attachedFiles.map((item: AttachedFileView) => item.file);

        if (!text && !files.length) {
            return;
        }

        if (files.length) {
            this.messageSent.emit({ type: 'file', files, text: text || undefined });
        } else {
            this.messageSent.emit({ type: 'text', text });
        }

        this.resetInputState();
    }

    /** Запрашивает отмену текущей генерации. */
    public cancel(): void {
        this.cancelRequested.emit();
    }

    /** Открывает нативный выборщик файлов. */
    public openFilePicker(): void {
        if (!this.allowFiles || this.isRecording) {
            return;
        }

        if (this.attachedFiles.length >= MAX_ATTACHED_FILES) {
            this.alertService.show(`Можно прикрепить не более ${MAX_ATTACHED_FILES} файлов.`);
            return;
        }

        const input = this.fileInputEl?.nativeElement;
        if (!input) {
            return;
        }

        input.accept = this.buildAcceptAttribute();
        input.click();
    }

    /** Обрабатывает выбор файлов пользователем. */
    public onFilesSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files ?? []);
        input.value = '';

        this.addFiles(files);
    }

    public onPaste(event: ClipboardEvent): void {
        const files = Array.from(event.clipboardData?.files ?? []);
        if (!this.allowFiles || this.isRecording || !files.length) {
            return;
        }

        event.preventDefault();
        this.addFiles(files);
    }

    public addFiles(files: File[]): void {
        if (!this.allowFiles || this.isRecording || !files.length) {
            return;
        }

        const acceptedTypes = this.stateStore.getState()?.attachmentsAcceptedTypes;
        if (!acceptedTypes?.types?.length) {
            this.alertService.show('Не удалось проверить допустимые типы файлов. Попробуйте позже.');
            return;
        }

        const validFiles: AttachedFileView[] = [];

        for (const file of files) {
            const rejectionReason = this.getFileRejectionReason(file, acceptedTypes);
            if (rejectionReason) {
                this.alertService.show(rejectionReason);
                continue;
            }

            validFiles.push({
                file,
                id: `${this.nextAttachedFileId++}`,
            });
        }

        if (!validFiles.length) {
            return;
        }

        const freeSlots = MAX_ATTACHED_FILES - this.attachedFiles.length;
        if (freeSlots <= 0) {
            this.alertService.show(`Можно прикрепить не более ${MAX_ATTACHED_FILES} файлов.`);
            return;
        }

        if (validFiles.length > freeSlots) {
            this.alertService.show(`Можно прикрепить не более ${MAX_ATTACHED_FILES} файлов.`);
        }

        this.attachedFiles = [...this.attachedFiles, ...validFiles.slice(0, freeSlots)];
        this.cdr.markForCheck();
    }

    /** Удаляет файл из preview списка. */
    public removeFile(fileId: string): void {
        this.attachedFiles = this.attachedFiles.filter((file: AttachedFileView) => file.id !== fileId);
        this.cdr.markForCheck();
    }

    private getFileRejectionReason(
        file: File,
        acceptedTypes: AiChatAttachmentsAcceptedTypes,
    ): string | null {
        if (file.size > acceptedTypes.maxBytes) {
            return `Файл «${file.name}» слишком большой. Максимум ${this.getFileSizeLabel(acceptedTypes.maxBytes)}.`;
        }

        if (!this.isFileTypeAccepted(file, acceptedTypes)) {
            const extensions = acceptedTypes.types
                .flatMap((type) => type.extensions)
                .join(', ');
            return `Файл «${file.name}» имеет недопустимый тип. Разрешены: ${extensions}.`;
        }

        return null;
    }

    private isFileTypeAccepted(file: File, acceptedTypes: AiChatAttachmentsAcceptedTypes): boolean {
        const mime = (file.type || '').toLowerCase();
        const extension = this.getFileExtension(file.name);

        return acceptedTypes.types.some((type) => {
            const mimeMatched = Boolean(mime) && type.mime.toLowerCase() === mime;
            const extensionMatched = Boolean(extension)
                && type.extensions.some((item) => item.toLowerCase() === extension);

            return mimeMatched || extensionMatched;
        });
    }

    private getFileExtension(fileName: string): string | null {
        const dotIndex = fileName.lastIndexOf('.');
        if (dotIndex < 0 || dotIndex === fileName.length - 1) {
            return null;
        }

        return fileName.slice(dotIndex).toLowerCase();
    }

    private buildAcceptAttribute(): string {
        const acceptedTypes = this.stateStore.getState()?.attachmentsAcceptedTypes;
        if (!acceptedTypes?.types?.length) {
            return '';
        }

        const values = new Set<string>();
        for (const type of acceptedTypes.types) {
            values.add(type.mime);
            for (const extension of type.extensions) {
                values.add(extension);
            }
        }

        return Array.from(values).join(',');
    }

    /** Возвращает подпись файла для preview. */
    public getFileSizeLabel(size: number): string {
        if (size < 1024) {
            return `${size} Б`;
        }

        if (size < 1024 * 1024) {
            return `${(size / 1024).toFixed(1)} КБ`;
        }

        return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
    }

    /** Начинает запись при нажатии на кнопку микрофона. */
    public onMicPointerDown(event: PointerEvent): void {
        event.preventDefault();

        this.hideHoldTooltip();

        if (this.isRecording) {
            return;
        }

        this.isRecording = true;
        this.recordingStartTime = Date.now();
        this.recordingElapsedLabel = '0.0 с';
        this.limitWarningShown = false;
        this.textControl.disable({ emitEvent: false });
        this.getActiveTextarea()?.blur();
        this.startRecordingTimer();
        this.cdr.markForCheck();

        this.zone.runOutsideAngular(() => {
            this.docUpHandler = () => {
                this.removeDocumentListeners();
                this.zone.run(() => void this.stopAndSend());
            };
            this.docCancelHandler = () => {
                this.removeDocumentListeners();
                this.zone.run(() => this.resetRecordingState());
            };
            document.addEventListener('pointerup', this.docUpHandler, { once: true });
            document.addEventListener('pointercancel', this.docCancelHandler, { once: true });
        });

        this.processor = new MicrophoneProcessor({
            maxRecordTime: MAX_VOICE_DURATION_MS,
            bitRate: VOICE_BIT_RATE,
        });
        this.processor.record().pipe(takeUntil(this.destroy$)).subscribe({
            error: (err: unknown) => {
                if (err instanceof LimitRecordException) {
                    this.finishWithBlob(err.getTrackFile());
                } else {
                    this.resetRecordingState();
                }
            },
        });
    }

    private async stopAndSend(): Promise<void> {
        if (!this.isRecording || this.processor === null) {
            return;
        }

        const blob = await this.processor.stop();
        this.finishWithBlob(blob);
    }

    private finishWithBlob(blob: Blob | null): void {
        const duration = this.recordingStartTime !== null ? Date.now() - this.recordingStartTime : 0;

        this.resetRecordingState();

        if (duration < MIN_VOICE_DURATION_MS) {
            this.showHoldTooltipHint();
            return;
        }

        if (blob !== null) {
            const files = this.attachedFiles.map((item: AttachedFileView) => item.file);
            this.messageSent.emit({
                type: 'voice',
                audio: blob,
                ...(files.length ? { files } : {}),
            });
            this.resetInputState();
        }
    }

    private resetRecordingState(): void {
        this.removeDocumentListeners();
        this.stopRecordingTimer();
        this.processor?.destroy();
        this.processor = null;
        this.recordingStartTime = null;
        this.isRecording = false;
        this.recordingElapsedLabel = '0.0 с';
        this.limitWarningShown = false;
        this.textControl.enable({ emitEvent: false });
        this.cdr.markForCheck();
    }

    private startRecordingTimer(): void {
        this.stopRecordingTimer();
        this.zone.runOutsideAngular(() => {
            this.recordingTimerId = setInterval(() => {
                if (this.recordingStartTime === null) {
                    return;
                }

                const elapsedMs = Date.now() - this.recordingStartTime;
                const label = `${(elapsedMs / 1000).toFixed(1)} с`;

                if (!this.limitWarningShown && elapsedMs >= VOICE_LIMIT_WARNING_AT_MS) {
                    this.limitWarningShown = true;
                    this.zone.run(() => {
                        this.recordingElapsedLabel = label;
                        this.showMicTooltip(LIMIT_TOOLTIP_TEXT, LIMIT_TOOLTIP_DURATION_MS);
                    });
                    return;
                }

                this.zone.run(() => {
                    this.recordingElapsedLabel = label;
                    this.cdr.markForCheck();
                });
            }, 100);
        });
    }

    private stopRecordingTimer(): void {
        if (this.recordingTimerId !== null) {
            clearInterval(this.recordingTimerId);
            this.recordingTimerId = null;
        }
    }

    private showHoldTooltipHint(): void {
        this.showMicTooltip(HOLD_TOOLTIP_TEXT, HOLD_TOOLTIP_DURATION_MS);
    }

    private showMicTooltip(text: string, durationMs: number): void {
        this.clearHoldTooltipTimer();
        this.holdTooltipText = text;
        this.showHoldTooltip = true;
        this.cdr.markForCheck();

        this.holdTooltipTimeoutId = setTimeout(() => {
            this.holdTooltipTimeoutId = null;
            this.showHoldTooltip = false;
            this.cdr.markForCheck();
        }, durationMs);
    }

    private hideHoldTooltip(): void {
        if (!this.showHoldTooltip && this.holdTooltipTimeoutId === null) {
            return;
        }

        this.clearHoldTooltipTimer();
        this.showHoldTooltip = false;
        this.cdr.markForCheck();
    }

    private clearHoldTooltipTimer(): void {
        if (this.holdTooltipTimeoutId !== null) {
            clearTimeout(this.holdTooltipTimeoutId);
            this.holdTooltipTimeoutId = null;
        }
    }

    private removeDocumentListeners(): void {
        if (this.docUpHandler !== null) {
            document.removeEventListener('pointerup', this.docUpHandler);
            this.docUpHandler = null;
        }

        if (this.docCancelHandler !== null) {
            document.removeEventListener('pointercancel', this.docCancelHandler);
            this.docCancelHandler = null;
        }
    }

    private syncExpandedState(): void {
        const fakeTextElement = this.fakeTextEl?.nativeElement;

        if (!fakeTextElement) {
            return;
        }

        const nextExpanded = this.isMultiline(fakeTextElement);
        const activeElement = this.getActiveTextarea();
        const selectionStart = activeElement?.selectionStart ?? 0;
        const selectionEnd = activeElement?.selectionEnd ?? 0;
        const isStateChanged = this.isExpanded !== nextExpanded;

        this.isExpanded = nextExpanded;
        if (isStateChanged) {
            this.cdr.detectChanges();
        }

        const nextElement = this.getActiveTextarea();

        if (nextElement !== null && document.activeElement !== nextElement) {
            nextElement.focus();
            nextElement.setSelectionRange(selectionStart, selectionEnd);
        }
    }

    private syncTextareaHeight(): void {
        const activeElement = this.getActiveTextarea();

        if (activeElement === null) {
            return;
        }

        activeElement.style.height = 'auto';
        activeElement.style.height = this.isExpanded ? `${activeElement.scrollHeight}px` : 'auto';
        this.cdr.markForCheck();
    }

    private getActiveTextarea(): HTMLTextAreaElement | null {
        return this.isExpanded
            ? this.textareaExpandedEl?.nativeElement ?? null
            : this.textareaEl?.nativeElement ?? null;
    }

    private isMultiline(el: HTMLDivElement): boolean {
        const styles = window.getComputedStyle(el);
        const lineHeight = Number.parseFloat(styles.lineHeight) || 24;
        const height = el.clientHeight;

        return height > Math.ceil(lineHeight * 1.5);
    }

    private resetInputState(): void {
        this.textControl.setValue('');
        this.attachedFiles = [];
        this.isExpanded = false;
        this.syncTextareaHeight();
        this.cdr.markForCheck();
    }
}
